/**
 * BroadcastDashboard — OBS Studio control panel.
 *
 * Mounted inside StudioPortal, ChurchModule, and (as a modal) LiveClassroomRoom.
 * Surfaces:
 *   - OBS connection picker (multi-site)
 *   - Connection settings dialog (host / port / encrypted password / destinations)
 *   - Live status (stream/record duration, current scene)
 *   - Action bar: Start/Stop Stream, Start/Stop Record, Switch Scene
 *   - Push text source (sermon title / scripture / lower-third)
 *   - Recording ingest (upload finished OBS file → auto-transcribe → media library)
 *   - Audit log tail
 *
 * Props: tenantId (required), siteId, studioId, linkModule, linkId
 *   linkModule/linkId pre-fill the ingest form so a sermon page can drop a
 *   recording straight into church_sermons, a class can attach to a session, etc.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Activity, Wifi, WifiOff, Circle, Square, Radio, Plus, RefreshCw, Upload, Trash2, Settings as SettingsIcon } from 'lucide-react';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';

const fmtDuration = (ms) => {
  if (!ms || ms < 0) return '00:00:00';
  const s = Math.floor(ms / 1000);
  const hh = String(Math.floor(s / 3600)).padStart(2, '0');
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
};

export default function BroadcastDashboard({ tenantId, siteId, studioId, linkModule, linkId }) {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data: connData, isLoading: connLoading, refetch: refetchConns } = useQuery({
    queryKey: ['obs-connections', tenantId, siteId, studioId],
    queryFn: () => api.get('/obs/connections', { params: { tenant_id: tenantId, site_id: siteId, studio_id: studioId } }).then(r => r.data),
    enabled: !!tenantId,
  });
  const connections = connData?.connections || [];

  // Auto-select first connection
  useEffect(() => {
    if (!selectedId && connections.length > 0) setSelectedId(connections[0].id);
  }, [connections, selectedId]);

  const selected = connections.find(c => c.id === selectedId);

  return (
    <div className="space-y-4" data-testid="broadcast-dashboard">
      {/* Connection selector + actions */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="flex items-center gap-2 text-base">
              <Radio className="h-4 w-4 text-fuchsia-600" /> OBS Broadcast Control
            </CardTitle>
            <div className="flex items-center gap-2">
              {connections.length > 0 && (
                <Select value={selectedId || ''} onValueChange={setSelectedId}>
                  <SelectTrigger className="w-full max-w-[220px]" data-testid="obs-connection-select">
                    <SelectValue placeholder="Select OBS connection" />
                  </SelectTrigger>
                  <SelectContent>
                    {connections.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.label} {c.last_status === 'online' ? '· online' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button size="sm" variant="outline" onClick={() => { setEditing(null); setSettingsOpen(true); }} data-testid="obs-add-connection-btn">
                <Plus className="h-4 w-4 mr-1" /> Add OBS
              </Button>
              {selected && (
                <Button size="sm" variant="outline" onClick={() => { setEditing(selected); setSettingsOpen(true); }} data-testid="obs-edit-connection-btn">
                  <SettingsIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {connLoading ? (
            <p className="text-xs text-slate-500">Loading connections…</p>
          ) : connections.length === 0 ? (
            <div className="text-sm text-slate-500 border border-dashed rounded-md p-6 text-center" data-testid="obs-empty-state">
              No OBS instances connected yet. Click <strong>Add OBS</strong> to register your studio&apos;s OBS WebSocket endpoint.
              <p className="text-xs text-slate-400 mt-2">
                In OBS: <em>Tools → WebSocket Server Settings</em>, enable the server (port 4455), copy the password, then paste it here.
              </p>
            </div>
          ) : selected ? (
            <ControlSurface conn={selected} linkModule={linkModule} linkId={linkId} refetchConns={refetchConns} />
          ) : null}
        </CardContent>
      </Card>

      <ConnectionDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        editing={editing}
        tenantId={tenantId}
        siteId={siteId}
        studioId={studioId}
        onSaved={() => { setSettingsOpen(false); refetchConns(); }}
      />
    </div>
  );
}

function ControlSurface({ conn, linkModule, linkId, refetchConns }) {
  const qc = useQueryClient();

  const { data: status, refetch: refetchStatus } = useQuery({
    queryKey: ['obs-status', conn.id],
    queryFn: () => api.get(`/obs/connections/${conn.id}/status`).then(r => r.data),
    refetchInterval: 3000,
  });

  const testMut = useMutation({
    mutationFn: () => api.post(`/obs/connections/${conn.id}/test`).then(r => r.data),
    onSuccess: (d) => {
      if (d.success) toast.success(`OBS reachable · v${d.obs_version}`);
      else toast.error(d.error || 'Connection failed');
      refetchConns(); refetchStatus();
    },
  });

  const actionMut = useMutation({
    mutationFn: ({ path, body }) => api.post(`/obs/connections/${conn.id}/${path}`, body || {}).then(r => r.data),
    onSuccess: (d, vars) => {
      if (d.success) toast.success(`OK · ${vars.path}`);
      else toast.error(d.error || `${vars.path} failed`);
      refetchStatus();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const isLive = !!status?.streaming;
  const isRec = !!status?.recording;
  const reachable = status?.success !== false;

  return (
    <Tabs defaultValue="controls" className="w-full">
      <TabsList className="grid w-full max-w-2xl grid-cols-2 sm:grid-cols-4">
        <TabsTrigger value="controls" data-testid="broadcast-tab-controls">Controls</TabsTrigger>
        <TabsTrigger value="overlays" data-testid="broadcast-tab-overlays">Overlays</TabsTrigger>
        <TabsTrigger value="ingest" data-testid="broadcast-tab-ingest">Recordings</TabsTrigger>
        <TabsTrigger value="audit" data-testid="broadcast-tab-audit">Audit Log</TabsTrigger>
      </TabsList>

      <TabsContent value="controls" className="mt-4 space-y-4">
        {/* Status pills */}
        <div className="flex flex-wrap items-center gap-2">
          {reachable ? (
            <Badge variant="outline" className="border-emerald-300 text-emerald-700"><Wifi className="h-3 w-3 mr-1" /> OBS online</Badge>
          ) : (
            <Badge variant="outline" className="border-rose-300 text-rose-700"><WifiOff className="h-3 w-3 mr-1" /> Unreachable</Badge>
          )}
          {isLive && <Badge className="bg-rose-600 text-white"><Circle className="h-2.5 w-2.5 mr-1 fill-current animate-pulse" /> LIVE {fmtDuration(status?.stream_duration_ms)}</Badge>}
          {isRec && <Badge className="bg-indigo-600 text-white"><Circle className="h-2.5 w-2.5 mr-1 fill-current" /> REC {fmtDuration(status?.record_duration_ms)}</Badge>}
          {status?.current_scene && (
            <Badge variant="outline" data-testid="obs-current-scene">Scene: {status.current_scene}</Badge>
          )}
          <Button size="sm" variant="ghost" onClick={() => { testMut.mutate(); }} data-testid="obs-test-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${testMut.isPending ? 'animate-spin' : ''}`} /> Test
          </Button>
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Button
            disabled={!reachable || actionMut.isPending}
            onClick={() => actionMut.mutate({ path: isLive ? 'stream/stop' : 'stream/start' })}
            className={isLive ? 'bg-rose-600 hover:bg-rose-700' : 'bg-fuchsia-600 hover:bg-fuchsia-700'}
            data-testid="obs-stream-toggle-btn"
          >
            <Radio className="h-4 w-4 mr-1" /> {isLive ? 'Stop Stream' : 'Start Stream'}
          </Button>
          <Button
            disabled={!reachable || actionMut.isPending}
            variant="outline"
            onClick={() => actionMut.mutate({ path: isRec ? 'record/stop' : 'record/start' })}
            data-testid="obs-record-toggle-btn"
          >
            {isRec ? <Square className="h-4 w-4 mr-1 fill-current" /> : <Circle className="h-4 w-4 mr-1" />}
            {isRec ? 'Stop Record' : 'Start Record'}
          </Button>
          <SceneSwitcher conn={conn} scenes={status?.scenes || []} disabled={!reachable} />
          <div />
        </div>

        {/* Destinations checklist */}
        {conn.destinations?.length > 0 && (
          <div>
            <Label className="text-xs text-slate-500">Configured destinations (in OBS)</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {conn.destinations.map((d, i) => (
                <Badge key={i} variant="outline" className="text-xs">{d.label || d.platform || d.url}</Badge>
              ))}
            </div>
          </div>
        )}
      </TabsContent>

      <TabsContent value="overlays" className="mt-4">
        <TextSourcePanel conn={conn} disabled={!reachable} />
      </TabsContent>

      <TabsContent value="ingest" className="mt-4">
        <RecordingIngestPanel conn={conn} linkModule={linkModule} linkId={linkId} />
      </TabsContent>

      <TabsContent value="audit" className="mt-4">
        <AuditLogPanel conn={conn} />
      </TabsContent>
    </Tabs>
  );
}

function SceneSwitcher({ conn, scenes, disabled }) {
  const [scene, setScene] = useState('');
  const mut = useMutation({
    mutationFn: () => api.post(`/obs/connections/${conn.id}/scene`, { scene_name: scene }).then(r => r.data),
    onSuccess: (d) => d.success ? toast.success(`Switched to ${scene}`) : toast.error(d.error || 'Failed'),
  });
  return (
    <div className="flex gap-1">
      <Select value={scene} onValueChange={setScene} disabled={disabled || scenes.length === 0}>
        <SelectTrigger data-testid="obs-scene-select"><SelectValue placeholder="Switch scene…" /></SelectTrigger>
        <SelectContent>
          {scenes.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button size="icon" variant="outline" disabled={!scene || disabled} onClick={() => mut.mutate()} data-testid="obs-scene-switch-btn">
        →
      </Button>
    </div>
  );
}

function TextSourcePanel({ conn, disabled }) {
  const [sourceName, setSourceName] = useState('sermon_title');
  const [text, setText] = useState('');

  const presets = [
    { key: 'sermon_title', label: 'Sermon Title' },
    { key: 'scripture', label: 'Scripture Reference' },
    { key: 'speaker_name', label: 'Speaker Name' },
    { key: 'lower_third', label: 'Lower Third' },
    { key: 'announcement', label: 'Announcement' },
    { key: 'countdown', label: 'Countdown Label' },
    { key: 'captions', label: 'Captions Overlay' },
  ];

  const mut = useMutation({
    mutationFn: () => api.post(`/obs/connections/${conn.id}/text`, { source_name: sourceName, text }).then(r => r.data),
    onSuccess: (d) => d.success ? toast.success('Pushed to OBS') : toast.error(d.error || 'Failed'),
  });

  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">OBS text source name</Label>
          <Select value={sourceName} onValueChange={setSourceName}>
            <SelectTrigger data-testid="obs-text-source-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {presets.map(p => <SelectItem key={p.key} value={p.key}>{p.label} ({p.key})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Custom source name</Label>
          <Input value={sourceName} onChange={(e) => setSourceName(e.target.value)} placeholder="e.g. lower_third_left" data-testid="obs-text-source-input" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Text to display in OBS</Label>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="e.g. John 3:16 — For God so loved the world…" data-testid="obs-text-input" />
      </div>
      <Button disabled={disabled || !text.trim() || mut.isPending} onClick={() => mut.mutate()} data-testid="obs-text-push-btn">
        Push to OBS
      </Button>
      <p className="text-xs text-slate-400">
        Tip: in OBS, add a <em>Text (GDI+/FreeType)</em> source named exactly as above and place it on your scene.
      </p>
    </div>
  );
}

function RecordingIngestPanel({ conn, linkModule, linkId }) {
  const qc = useQueryClient();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [autoTranscribe, setAutoTranscribe] = useState(true);
  const [lm, setLm] = useState(linkModule || 'studio_recording');
  const [lid, setLid] = useState(linkId || '');

  const { data: recData, refetch } = useQuery({
    queryKey: ['obs-recordings', conn.tenant_id],
    queryFn: () => api.get('/obs/recordings', { params: { tenant_id: conn.tenant_id } }).then(r => r.data),
  });

  const mut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append('file', file);
      if (title) fd.append('title', title);
      fd.append('auto_transcribe', String(autoTranscribe));
      if (lm) fd.append('link_module', lm);
      if (lid) fd.append('link_id', lid);
      return api.post(`/obs/connections/${conn.id}/recording/ingest`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then(r => r.data);
    },
    onSuccess: () => {
      toast.success('Recording uploaded to Media Library');
      setFile(null); setTitle('');
      refetch();
      qc.invalidateQueries({ queryKey: ['transcription-jobs'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-4">
      <div className="border rounded-md p-4 space-y-3">
        <Label className="text-sm font-semibold">Ingest a finished OBS recording</Label>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input type="file" accept="video/*,audio/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="obs-ingest-file" />
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (defaults to filename)" data-testid="obs-ingest-title" />
          <Select value={lm} onValueChange={setLm}>
            <SelectTrigger data-testid="obs-ingest-link-module"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="studio_recording">Studio Recording</SelectItem>
              <SelectItem value="church_sermons">Church Sermon</SelectItem>
              <SelectItem value="live_classes">Live Class</SelectItem>
              <SelectItem value="event">Event</SelectItem>
              <SelectItem value="campaign">Campaign</SelectItem>
            </SelectContent>
          </Select>
          <Input value={lid} onChange={(e) => setLid(e.target.value)} placeholder="Link ID (optional)" data-testid="obs-ingest-link-id" />
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={autoTranscribe} onCheckedChange={setAutoTranscribe} data-testid="obs-ingest-transcribe-toggle" />
          <Label className="text-sm cursor-pointer" onClick={() => setAutoTranscribe(!autoTranscribe)}>Auto-transcribe with Whisper</Label>
        </div>
        <Button disabled={!file || mut.isPending} onClick={() => mut.mutate()} data-testid="obs-ingest-upload-btn">
          <Upload className="h-4 w-4 mr-1" /> {mut.isPending ? 'Uploading…' : 'Upload to Media Library'}
        </Button>
      </div>

      <div>
        <Label className="text-xs text-slate-500">Recent ingested recordings</Label>
        <div className="mt-2 space-y-1">
          {(recData?.recordings || []).slice(0, 10).map(r => (
            <div key={r.id} className="text-xs flex items-center justify-between border rounded px-2 py-1" data-testid={`obs-recording-${r.id}`}>
              <span className="truncate">{r.title}</span>
              <span className="text-slate-400">{Math.round((r.size_bytes || 0) / 1024 / 1024)} MB</span>
            </div>
          ))}
          {!recData?.recordings?.length && <p className="text-xs text-slate-400">No recordings yet.</p>}
        </div>
      </div>
    </div>
  );
}

function AuditLogPanel({ conn }) {
  const { data } = useQuery({
    queryKey: ['obs-audit', conn.id],
    queryFn: () => api.get(`/obs/connections/${conn.id}/audit`).then(r => r.data),
    refetchInterval: 5000,
  });
  return (
    <div className="text-xs space-y-1 max-h-80 overflow-auto" data-testid="obs-audit-log">
      {(data?.logs || []).map(l => (
        <div key={l.id} className="flex items-center justify-between border-b py-1">
          <span className="truncate">
            <Badge variant="outline" className="mr-1 text-[10px]">{l.result || 'ok'}</Badge>
            <span className="font-mono">{l.action}</span>
            {l.error && <span className="text-rose-600 ml-2">{l.error}</span>}
          </span>
          <span className="text-slate-400">{l.at?.replace('T', ' ').slice(0, 19)}</span>
        </div>
      ))}
      {!data?.logs?.length && <p className="text-slate-400">No audit entries yet.</p>}
    </div>
  );
}

function ConnectionDialog({ open, onClose, editing, tenantId, siteId, studioId, onSaved }) {
  const [form, setForm] = useState({
    label: '', host: '127.0.0.1', port: 4455, password: '', destinations: [],
  });
  const [newDest, setNewDest] = useState({ label: '', platform: 'youtube', url: '' });

  useEffect(() => {
    if (editing) setForm({
      label: editing.label || '', host: editing.host || '127.0.0.1', port: editing.port || 4455,
      password: '', destinations: editing.destinations || [],
    });
    else setForm({ label: '', host: '127.0.0.1', port: 4455, password: '', destinations: [] });
  }, [editing, open]);

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = {
        tenant_id: tenantId, site_id: siteId, studio_id: studioId,
        label: form.label, host: form.host, port: Number(form.port),
        destinations: form.destinations, is_active: true,
      };
      // Only send password if user typed one (avoid clearing on edit)
      if (form.password) payload.password = form.password;
      if (editing) {
        return api.put(`/obs/connections/${editing.id}`, payload).then(r => r.data);
      }
      return api.post('/obs/connections', payload).then(r => r.data);
    },
    onSuccess: () => { toast.success(editing ? 'OBS connection updated' : 'OBS connection added'); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const delMut = useMutation({
    mutationFn: () => api.delete(`/obs/connections/${editing.id}`).then(r => r.data),
    onSuccess: () => { toast.success('OBS connection deleted'); onSaved(); },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="obs-connection-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit OBS Connection' : 'Add OBS Connection'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Label</Label>
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Cathedral Studio OBS" data-testid="obs-dialog-label" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div className="col-span-2">
              <Label className="text-xs">WebSocket host</Label>
              <Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="127.0.0.1" data-testid="obs-dialog-host" />
            </div>
            <div>
              <Label className="text-xs">Port</Label>
              <Input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} data-testid="obs-dialog-port" />
            </div>
          </div>
          <div>
            <Label className="text-xs">WebSocket password {editing && <span className="text-slate-400">(leave blank to keep existing)</span>}</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" data-testid="obs-dialog-password" />
          </div>
          <div>
            <Label className="text-xs">Stream destinations (for reference)</Label>
            <div className="space-y-1">
              {form.destinations.map((d, i) => (
                <div key={i} className="flex items-center gap-1 text-xs border rounded px-2 py-1">
                  <Badge variant="outline" className="text-[10px]">{d.platform}</Badge>
                  <span className="flex-1 truncate">{d.label || d.url}</span>
                  <Button size="icon" variant="ghost" onClick={() => setForm({ ...form, destinations: form.destinations.filter((_, j) => j !== i) })}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 mt-1">
              <Select value={newDest.platform} onValueChange={(v) => setNewDest({ ...newDest, platform: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="youtube">YouTube</SelectItem>
                  <SelectItem value="facebook">Facebook</SelectItem>
                  <SelectItem value="rtmp">RTMP</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
              <Input value={newDest.label} onChange={(e) => setNewDest({ ...newDest, label: e.target.value })} placeholder="Label" className="text-xs" />
              <Button size="sm" variant="outline" disabled={!newDest.label} onClick={() => { setForm({ ...form, destinations: [...form.destinations, newDest] }); setNewDest({ label: '', platform: 'youtube', url: '' }); }}>
                Add
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter className="flex justify-between sm:justify-between">
          {editing ? (
            <Button variant="destructive" size="sm" onClick={() => delMut.mutate()} data-testid="obs-dialog-delete-btn">
              Delete
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button disabled={!form.label || saveMut.isPending} onClick={() => saveMut.mutate()} data-testid="obs-dialog-save-btn">
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
